import { useState, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { IconCheck, IconChevronDown } from "@tabler/icons-react";
import { useFleetYawMeasurements } from "@/api/hooks/use-fleet-measurements";
import { useEvent } from "@/api/hooks";
import { TeamFlag } from "@/components/team-flag";
import { timeAgo } from "@/lib/time";
import {
  computeSide,
  computeYawStatus,
  extractSide,
  yawToPercent,
  TARGET_YAW_ANGLE,
  YAW_LABEL_DISPLAY,
  yawLabelBadgeClass,
  isBaselineLabel,
  BASELINE_ROW_CLASS,
  BASELINE_ACCENT_CLASS,
  type SideComputed,
  type YawStatus,
} from "@/domain/yaw-check";
import { asEventId, type EventId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";
import { CheckedCard, ComparisonTeamPills, FLEET_ROW_INNER, teamColor, useToggleSet, type ChartTooltip } from "./_shared";

type FleetYawTeamEntry = components["schemas"]["FleetYawTeamEntry-Output"];
type YawCheckResponse = components["schemas"]["YawCheckResponse"];

function statusMeta(s: YawStatus | "none") {
  const map = {
    "in-spec":  { label: "In spec",  bg: "bg-emerald-50 dark:bg-emerald-950/40",  text: "text-emerald-700 dark:text-emerald-400", border: "border-l-green-500" },
    "high":     { label: "High",     bg: "bg-amber-50 dark:bg-amber-950/40",    text: "text-amber-700 dark:text-amber-400",   border: "border-l-amber-400" },
    "critical": { label: "Critical", bg: "bg-red-50 dark:bg-red-950/40",      text: "text-red-700 dark:text-red-400",     border: "border-l-red-400" },
    "none":     { label: "No data",  bg: "bg-muted/50",     text: "text-muted-foreground",    border: "border-l-border" },
  };
  return map[s];
}

const STATUS_COLORS: Record<YawStatus, string> = {
  "in-spec": "#22c55e",
  "high": "#f59e0b",
  "critical": "#ef4444",
};

function gaugeColor(staticYaw: number): string {
  return STATUS_COLORS[computeYawStatus(staticYaw)];
}

function MiniGauge({ computed }: { computed: SideComputed }) {
  const pos = yawToPercent(computed.tot_yaw);
  const minP = yawToPercent(computed.tot_min_yaw);
  const maxP = yawToPercent(computed.tot_max_yaw);
  const color = gaugeColor(computed.tot_yaw);
  const rw = maxP - minP;

  return (
    <div className="relative w-full" style={{ paddingTop: 6 }}>
      <div className="absolute" style={{ left: `${pos}%`, top: 0, transform: "translateX(-3.5px)" }}>
        <svg width="9" height="6" viewBox="-1 -1 9 7"><path d="M0 0 L3.5 5 L7 0Z" fill={color} stroke="rgba(0,0,0,0.5)" strokeWidth="0.75" strokeLinejoin="round" /></svg>
      </div>
      <div className="relative h-3 rounded-[6px] overflow-hidden w-full">
        <div className="absolute inset-0" style={{
          background: "linear-gradient(to right, #ef4444 0%, #f59e0b 20%, #22c55e 40%, #22c55e 60%, #f59e0b 80%, #ef4444 100%)",
          opacity: 0.3,
        }} />
        <div className="absolute top-0 h-full" style={{ left: "50%", transform: "translateX(-1px)", borderLeft: "2px dashed rgba(0,0,0,0.6)" }} />
        <div className="absolute top-0 h-full rounded-[6px]" style={{ left: `${minP}%`, width: `${rw}%`, background: color, opacity: 0.6 }} />
      </div>
    </div>
  );
}

function GaugeRow({ check, side }: { check: YawCheckResponse; side: "prt" | "stb" }) {
  const m = extractSide(check, side);
  const c = computeSide(m);
  const labelColor = side === "prt" ? "text-red-400" : "text-emerald-500";

  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className={`text-[9px] font-bold w-5 shrink-0 ${labelColor}`}>{side === "prt" ? "PRT" : "STB"}</span>
      <div className="flex-1 min-w-[60px]">
        <MiniGauge computed={c} />
      </div>
      <div className="flex items-baseline gap-1 text-[11px] tabular-nums shrink-0">
        <span className="font-semibold text-foreground">{c.tot_yaw.toFixed(2)}°</span>
        <span className="text-[9px] text-muted-foreground">{c.tot_min_yaw.toFixed(2)}–{c.tot_max_yaw.toFixed(2)}</span>
      </div>
    </div>
  );
}

interface TrendData {
  event: string;
  eventDate: string;
  label?: string | null;
  checkedAt: string;
  checkId: string;
  prt: SideComputed;
  stb: SideComputed;
}

function computeTrendData(history: FleetYawTeamEntry["history"]): TrendData[] {
  return history.map((h) => ({
    event: h.event_name,
    eventDate: h.event_date,
    label: h.check.label,
    checkedAt: h.check.checked_at,
    checkId: h.check.id,
    prt: computeSide(extractSide(h.check, "prt")),
    stb: computeSide(extractSide(h.check, "stb")),
  }));
}

function InlineTrendSide({ data, sideKey, status }: { data: TrendData[]; sideKey: "prt" | "stb"; status: YawStatus }) {
  const w = 140, h = 20, px = 6, py = 3;
  if (!data.length) return <div style={{ width: w, height: h }} />;

  const yMin = 0, yMax = 0.6;
  const xf = (i: number) => data.length === 1 ? w / 2 : px + (i / (data.length - 1)) * (w - px * 2);
  const yf = (v: number) => h - py - ((v - yMin) / (yMax - yMin)) * (h - py * 2);
  const tY = yf(0.3);
  const sc = status === "in-spec" ? "#3b82f6" : status === "high" ? "#f59e0b" : "#ef4444";
  const bc = status === "in-spec" ? "rgba(59,130,246,0.12)" : status === "high" ? "rgba(245,158,11,0.12)" : "rgba(239,68,68,0.12)";

  const statics = data.map((d) => d[sideKey].tot_yaw);
  const mins = data.map((d) => d[sideKey].tot_min_yaw);
  const maxs = data.map((d) => d[sideKey].tot_max_yaw);

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block w-full">
      <line x1={px} y1={tY} x2={w - px} y2={tY} stroke="var(--border)" strokeWidth={0.5} strokeDasharray="2,2" />
      {data.length === 1 ? (
        <>
          <line x1={xf(0)} y1={yf(maxs[0])} x2={xf(0)} y2={yf(mins[0])} stroke={sc} strokeWidth={2} strokeLinecap="round" opacity={0.3} />
          <circle cx={xf(0)} cy={yf(statics[0])} r={2.5} fill={sc} stroke="var(--card)" strokeWidth={1} />
        </>
      ) : (
        <>
          <polygon points={[...data.map((_, i) => `${xf(i)},${yf(maxs[i])}`), ...data.map((_, i) => `${xf(data.length - 1 - i)},${yf(mins[data.length - 1 - i])}`),].join(" ")} fill={bc} />
          <path d={statics.map((v, i) => `${i === 0 ? "M" : "L"}${xf(i)},${yf(v)}`).join(" ")} fill="none" stroke={sc} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          {statics.map((v, i) => (
            <circle key={i} cx={xf(i)} cy={yf(v)} r={i === statics.length - 1 ? 2.5 : 1.5} fill={sc} stroke={i === statics.length - 1 ? "var(--card)" : "none"} strokeWidth={1} opacity={i === statics.length - 1 ? 1 : 0.5} />
          ))}
        </>
      )}
    </svg>
  );
}

function InlineTrend({ data, status }: { data: TrendData[]; status: YawStatus }) {
  if (!data.length) return <span className="text-[10px] text-muted-foreground italic">No history</span>;
  // PRT/STB are already labelled by the adjacent GaugeRow on the same line —
  // don't repeat them here (was the "PRT PRT" / "STB STB" doubling).
  return (
    <div className="flex flex-col gap-1">
      <InlineTrendSide data={data} sideKey="prt" status={status} />
      <InlineTrendSide data={data} sideKey="stb" status={status} />
    </div>
  );
}

/** Split a team's chronologically-sorted checks into the latest-per-event (mainPoints) and the rest (extraPoints). */
function splitMainAndExtras(data: TrendData[]): { mainPoints: TrendData[]; extraPoints: TrendData[] } {
  const groups = new Map<string, TrendData[]>();
  for (const d of data) {
    const list = groups.get(d.event) ?? [];
    list.push(d);
    groups.set(d.event, list);
  }
  const mainPoints: TrendData[] = [];
  const extraPoints: TrendData[] = [];
  for (const list of groups.values()) {
    const sorted = [...list].sort((a, b) => a.checkedAt.localeCompare(b.checkedAt));
    const main = sorted[sorted.length - 1];
    mainPoints.push(main);
    for (const d of sorted) if (d !== main) extraPoints.push(d);
  }
  return { mainPoints, extraPoints };
}

const EXTRA_COLOR = "#94a3b8";
const EXTRA_OFFSET_PX = 7;

/** Diamond marker — distinguishes re-cal (baseline) points from circular static points. */
function diamondPath(cx: number, cy: number, r: number): string {
  return `M${cx},${cy - r}L${cx + r},${cy}L${cx},${cy + r}L${cx - r},${cy}Z`;
}

function ComparisonSideChart({ teams, allEvents, sideKey }: {
  teams: { code: string; data: TrendData[] }[];
  allEvents: string[];
  sideKey: "prt" | "stb";
}) {
  const [tooltip, setTooltip] = useState<ChartTooltip | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const splitByTeam = useMemo(
    () => teams.map(({ code, data }) => ({ code, data, ...splitMainAndExtras(data) })),
    [teams],
  );

  const showTooltip = (e: React.MouseEvent, text: string) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top - 8, text });
  };

  const w = 400, h = 170, px = 40, py = 25;

  // Auto-range Y to fit every plotted value for this side — static + min/max,
  // for both the latest-per-event line and the non-latest extras — so nothing
  // is clipped. Keep 0–0.6° as the floor so the 0.3° target + in-spec band
  // stay visible; expand past it (snapped to the tick step) for any reading
  // outside that window.
  const ys = teams.flatMap(({ data }) =>
    data.flatMap((d) => [d[sideKey].tot_yaw, d[sideKey].tot_min_yaw, d[sideKey].tot_max_yaw]),
  );
  const rawMin = Math.min(0, ...ys);
  const rawMax = Math.max(0.6, ...ys);
  const step = rawMax - rawMin > 1.2 ? 0.2 : 0.1;
  const yMin = Math.floor(rawMin / step) * step;
  const yMax = Math.ceil(rawMax / step) * step;
  const ticks: number[] = [];
  for (let v = yMin; v <= yMax + 1e-9; v += step) ticks.push(Math.round(v * 100) / 100);

  const xf = (i: number) => allEvents.length === 1 ? w / 2 : px + (i / (allEvents.length - 1)) * (w - px * 2);
  const yf = (v: number) => h - py - ((v - yMin) / (yMax - yMin)) * (h - py * 2);
  const tY = yf(0.3), gTop = yf(0.45), gBot = yf(0.15);

  return (
    <div className="relative" onMouseLeave={() => setTooltip(null)}>
    {tooltip && (
      <div className="absolute z-10 px-2 py-1 rounded bg-gray-900 text-white text-[10px] tabular-nums whitespace-nowrap pointer-events-none" style={{ left: tooltip.x, top: tooltip.y, transform: "translate(-50%, -100%)" }}>
        {tooltip.text}
      </div>
    )}
    <svg ref={svgRef} viewBox={`0 0 ${w} ${h}`} className="block w-full" preserveAspectRatio="xMidYMid meet">
      <rect x={px} y={gTop} width={w - px * 2} height={gBot - gTop} fill="rgba(34,197,94,0.06)" />
      {ticks.map((v) => (
        <g key={v}>
          <text x={px - 6} y={yf(v) + 3} textAnchor="end" fill="var(--muted-foreground)" fontSize={9} style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>{v.toFixed(1)}°</text>
          <line x1={px} y1={yf(v)} x2={w - px} y2={yf(v)} stroke="var(--border)" strokeWidth={1} />
        </g>
      ))}
      <line x1={px} y1={tY} x2={w - px} y2={tY} stroke="#94a3b8" strokeWidth={1} strokeDasharray="5,4" />
      {allEvents.map((ev, i) => (
        <text key={ev} x={xf(i)} y={h - 5} textAnchor="middle" fill="var(--muted-foreground)" fontSize={9} style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>{ev}</text>
      ))}
      {splitByTeam.map(({ code, mainPoints }) => {
        const color = teamColor(code);
        const points = mainPoints.map((d) => {
          const idx = allEvents.indexOf(d.event);
          if (idx === -1) return null;
          const s = d[sideKey].tot_yaw, mn = d[sideKey].tot_min_yaw, mx = d[sideKey].tot_max_yaw;
          if (mn === 0 && s > 0.01) return { i: idx, s, mn: s, mx: s };
          return { i: idx, s, mn, mx };
        }).filter(Boolean) as { i: number; s: number; mn: number; mx: number }[];
        points.sort((a, b) => a.i - b.i);
        const hasRange = points.some((p) => Math.abs(p.mx - p.mn) > 0.001);
        if (points.length < 2 || !hasRange) {
          if (points.length === 1 && hasRange) return <line key={`w-${code}`} x1={xf(points[0].i)} y1={yf(points[0].mx)} x2={xf(points[0].i)} y2={yf(points[0].mn)} stroke={color} strokeWidth={4} strokeLinecap="round" opacity={0.15} />;
          return null;
        }
        return <polygon key={`b-${code}`} points={`${points.map((p) => `${xf(p.i)},${yf(p.mx)}`).join(" ")} ${[...points].reverse().map((p) => `${xf(p.i)},${yf(p.mn)}`).join(" ")}`} fill={color} opacity={0.1} />;
      })}
      {splitByTeam.map(({ code, mainPoints, extraPoints }) => {
        const color = teamColor(code);

        const main = mainPoints.map((d) => {
          const idx = allEvents.indexOf(d.event);
          if (idx === -1) return null;
          const side = d[sideKey];
          return { i: idx, v: side.tot_yaw, mn: side.tot_min_yaw, mx: side.tot_max_yaw, ev: d.event, label: d.label };
        }).filter(Boolean) as { i: number; v: number; mn: number; mx: number; ev: string; label?: string | null }[];
        main.sort((a, b) => a.i - b.i);

        const extrasByEvent = new Map<number, { v: number; mn: number; mx: number; ev: string; label?: string | null; checkId: string }[]>();
        for (const d of extraPoints) {
          const idx = allEvents.indexOf(d.event);
          if (idx === -1) continue;
          const side = d[sideKey];
          const list = extrasByEvent.get(idx) ?? [];
          list.push({ v: side.tot_yaw, mn: side.tot_min_yaw, mx: side.tot_max_yaw, ev: d.event, label: d.label, checkId: d.checkId });
          extrasByEvent.set(idx, list);
        }

        if (!main.length && !extrasByEvent.size) return null;
        return (
          <g key={`l-${code}`}>
            {main.length > 1 && (
              <path d={main.map((p, j) => `${j ? "L" : "M"}${xf(p.i)},${yf(p.v)}`).join(" ")} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
            )}
            {[...extrasByEvent.entries()].flatMap(([idx, list]) =>
              list.map((p, k) => {
                const hasRange = Math.abs(p.mx - p.mn) > 0.001 && !(p.mn === 0 && p.v > 0.01);
                const labelDisp = p.label ? ` (${YAW_LABEL_DISPLAY[p.label] ?? p.label})` : "";
                const isRecal = isBaselineLabel(p.label);
                // Extras step away from the event's column; the first event has
                // no room to its left, so its extras step right instead.
                const dir = idx === 0 && allEvents.length > 1 ? 1 : -1;
                const cx = xf(idx) + dir * (list.length - k) * EXTRA_OFFSET_PX;
                const tip = `${code} ${p.ev}${labelDisp}: ${p.v.toFixed(2)}° (${p.mn.toFixed(2)}–${p.mx.toFixed(2)}°)`;
                return (
                  <g key={`x-${p.checkId}`}>
                    {hasRange && (
                      <>
                        <circle cx={cx} cy={yf(p.mx)} r={2} fill="var(--card)" stroke={EXTRA_COLOR} strokeWidth={2} style={{ cursor: "pointer" }}
                          onMouseEnter={(e) => showTooltip(e, `${code} ${p.ev}${labelDisp} max: ${p.mx.toFixed(2)}°`)} />
                        <circle cx={cx} cy={yf(p.mn)} r={2} fill="var(--card)" stroke={EXTRA_COLOR} strokeWidth={2} style={{ cursor: "pointer" }}
                          onMouseEnter={(e) => showTooltip(e, `${code} ${p.ev}${labelDisp} min: ${p.mn.toFixed(2)}°`)} />
                      </>
                    )}
                    {isRecal ? (
                      <path d={diamondPath(cx, yf(p.v), 4)} fill="var(--card)" stroke={EXTRA_COLOR} strokeWidth={2} style={{ cursor: "pointer" }}
                        onMouseEnter={(e) => showTooltip(e, tip)} />
                    ) : (
                      <circle cx={cx} cy={yf(p.v)} r={3.5} fill="var(--card)" stroke={EXTRA_COLOR} strokeWidth={2} style={{ cursor: "pointer" }}
                        onMouseEnter={(e) => showTooltip(e, tip)} />
                    )}
                  </g>
                );
              })
            )}
            {main.map((p, j) => {
              const isLast = j === main.length - 1;
              const hasRange = Math.abs(p.mx - p.mn) > 0.001;
              const isRecal = isBaselineLabel(p.label);
              const tip = `${code} ${p.ev} static: ${p.v.toFixed(2)}° (${p.mn.toFixed(2)}–${p.mx.toFixed(2)}°)${isRecal ? " · Re-cal" : ""}`;
              return (
                <g key={`m-${j}`}>
                  {hasRange && (
                    <>
                      <circle cx={xf(p.i)} cy={yf(p.mx)} r={2} fill={color} opacity={0.4} style={{ cursor: "pointer" }}
                        onMouseEnter={(e) => showTooltip(e, `${code} ${p.ev} max: ${p.mx.toFixed(2)}°`)} />
                      <circle cx={xf(p.i)} cy={yf(p.mn)} r={2} fill={color} opacity={0.4} style={{ cursor: "pointer" }}
                        onMouseEnter={(e) => showTooltip(e, `${code} ${p.ev} min: ${p.mn.toFixed(2)}°`)} />
                    </>
                  )}
                  {isRecal ? (
                    <path d={diamondPath(xf(p.i), yf(p.v), isLast ? 5 : 3.5)} fill={color} stroke="var(--card)" strokeWidth={isLast ? 2 : 1.5} style={{ cursor: "pointer" }}
                      onMouseEnter={(e) => showTooltip(e, tip)} />
                  ) : (
                    <circle cx={xf(p.i)} cy={yf(p.v)} r={isLast ? 4.5 : 3} fill={color} stroke="var(--card)" strokeWidth={isLast ? 2 : 1.5} style={{ cursor: "pointer" }}
                      onMouseEnter={(e) => showTooltip(e, tip)} />
                  )}
                  {isLast && <text x={xf(p.i) + 7} y={yf(p.v) + 4} fill={color} fontSize={9} fontWeight={600} style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>{code} {p.v.toFixed(2)}°</text>}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
    </div>
  );
}

function HistoryTable({ entry }: { entry: FleetYawTeamEntry }) {
  if (!entry.history.length) return null;

  function delta(curr: number, prev: number | null): string {
    if (prev == null) return "";
    const d = curr - prev;
    if (Math.abs(d) < 0.005) return "—";
    const arrow = d > 0 ? "↑" : "↓";
    return `${arrow}${Math.abs(d).toFixed(2)}`;
  }

  function deltaClass(curr: number, prev: number | null): string {
    if (prev == null) return "";
    const d = curr - prev;
    if (Math.abs(d) < 0.005) return "text-muted-foreground/40";
    return Math.abs(curr - TARGET_YAW_ANGLE) < Math.abs(prev - TARGET_YAW_ANGLE) ? "text-emerald-500" : "text-red-400";
  }

  function rangeDelta(curr: number, prev: number | null): string {
    if (prev == null) return "";
    const d = curr - prev;
    if (Math.abs(d) < 0.005) return "—";
    const arrow = d > 0 ? "↑" : "↓";
    return `${arrow}${Math.abs(d).toFixed(2)}`;
  }

  function rangeDeltaClass(curr: number, prev: number | null): string {
    if (prev == null) return "";
    const d = curr - prev;
    if (Math.abs(d) < 0.005) return "text-muted-foreground/40";
    return d < 0 ? "text-emerald-500" : "text-red-400";
  }

  const VAL_BG: Record<YawStatus, string> = {
    "in-spec": "bg-emerald-50/60 dark:bg-emerald-950/40",
    "high": "bg-amber-50/60 dark:bg-amber-950/40",
    "critical": "bg-red-50/60 dark:bg-red-950/40",
  };
  function valBg(yaw: number) {
    return VAL_BG[computeYawStatus(yaw)];
  }

  const reversed = [...entry.history].reverse();

  return (
    <div className="overflow-x-auto -mx-4">
      <table className="w-full text-left min-w-[600px]">
        <thead>
          <tr className="border-b border-border">
            <th className="py-1.5 px-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider w-32" rowSpan={2}>Event</th>
            <th className="py-1 px-2 text-[10px] font-bold text-red-400 uppercase tracking-wider text-center border-b border-border/30" colSpan={4}>Port</th>
            <th className="py-1 px-2 text-[10px] font-bold text-emerald-500 uppercase tracking-wider text-center border-b border-border/30" colSpan={4}>Starboard</th>
          </tr>
          <tr className="border-b border-border">
            {["Static", "Min", "Max", "Range", "Static", "Min", "Max", "Range"].map((h, i) => (
              <th key={i} className="py-1 px-2 text-[9px] font-semibold text-muted-foreground uppercase text-center">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reversed.map((histEntry, ri) => {
            const ci = entry.history.length - 1 - ri;
            const prev = ci > 0 ? entry.history[ci - 1] : null;
            const prt = computeSide(extractSide(histEntry.check, "prt"));
            const stb = computeSide(extractSide(histEntry.check, "stb"));
            const prevPrt = prev ? computeSide(extractSide(prev.check, "prt")) : null;
            const prevStb = prev ? computeSide(extractSide(prev.check, "stb")) : null;
            const isLatest = ri === 0;
            const eventAbove = ri > 0 ? reversed[ri - 1]?.event_name : null;
            const hideEventName = eventAbove === histEntry.event_name;
            const eventBelow = ri < reversed.length - 1 ? reversed[ri + 1]?.event_name : null;
            const groupWithBelow = eventBelow === histEntry.event_name;
            const label = histEntry.check.label;
            const isRecal = isBaselineLabel(label);

            return (
              <tr key={histEntry.check.id} className={`${isLatest ? "bg-blue-50/30 dark:bg-blue-950/20" : isRecal ? BASELINE_ROW_CLASS : "hover:bg-muted/30"} ${groupWithBelow ? "border-b border-border/15" : "border-b border-border/30"}`}>
                <td className={`py-1.5 px-2 text-xs text-muted-foreground font-medium whitespace-nowrap ${isRecal ? BASELINE_ACCENT_CLASS : ""}`}>
                  {!hideEventName && histEntry.event_name}
                  {label && (
                    <span className={`ml-1 text-[9px] px-1 py-0.5 rounded font-medium ${yawLabelBadgeClass(label) ?? "bg-muted text-muted-foreground"}`}>
                      {YAW_LABEL_DISPLAY[label] ?? label}
                    </span>
                  )}
                  {isLatest && <span className="ml-1 text-[9px] text-blue-500 font-semibold uppercase">Latest</span>}
                </td>
                <td className="py-1.5 px-2 text-xs tabular-nums text-center whitespace-nowrap">
                  <span className={`inline-block px-1 py-0.5 rounded text-[11px] ${valBg(prt.tot_yaw)} font-semibold`}>{prt.tot_yaw.toFixed(2)}°</span>
                  <span className={`text-[9px] ml-0.5 font-medium ${deltaClass(prt.tot_yaw, prevPrt?.tot_yaw ?? null)}`}>{delta(prt.tot_yaw, prevPrt?.tot_yaw ?? null)}</span>
                </td>
                <td className="py-1.5 px-2 text-[11px] tabular-nums text-muted-foreground text-center">{prt.tot_min_yaw.toFixed(2)}</td>
                <td className="py-1.5 px-2 text-[11px] tabular-nums text-muted-foreground text-center">{prt.tot_max_yaw.toFixed(2)}</td>
                <td className="py-1.5 px-2 text-[11px] tabular-nums text-muted-foreground text-center whitespace-nowrap">
                  ±{prt.range.toFixed(2)}
                  <span className={`text-[9px] ml-0.5 font-medium ${rangeDeltaClass(prt.range, prevPrt?.range ?? null)}`}>{rangeDelta(prt.range, prevPrt?.range ?? null)}</span>
                </td>
                <td className="py-1.5 px-2 text-xs tabular-nums text-center whitespace-nowrap">
                  <span className={`inline-block px-1 py-0.5 rounded text-[11px] ${valBg(stb.tot_yaw)} font-semibold`}>{stb.tot_yaw.toFixed(2)}°</span>
                  <span className={`text-[9px] ml-0.5 font-medium ${deltaClass(stb.tot_yaw, prevStb?.tot_yaw ?? null)}`}>{delta(stb.tot_yaw, prevStb?.tot_yaw ?? null)}</span>
                </td>
                <td className="py-1.5 px-2 text-[11px] tabular-nums text-muted-foreground text-center">{stb.tot_min_yaw.toFixed(2)}</td>
                <td className="py-1.5 px-2 text-[11px] tabular-nums text-muted-foreground text-center">{stb.tot_max_yaw.toFixed(2)}</td>
                <td className="py-1.5 px-2 text-[11px] tabular-nums text-muted-foreground text-center whitespace-nowrap">
                  ±{stb.range.toFixed(2)}
                  <span className={`text-[9px] ml-0.5 font-medium ${rangeDeltaClass(stb.range, prevStb?.range ?? null)}`}>{rangeDelta(stb.range, prevStb?.range ?? null)}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function YawPage() {
  const { eventId: rawEventId } = useParams<{ eventId: string }>();
  const eventId = asEventId(rawEventId ?? "") as EventId;
  const { data: event } = useEvent(eventId);
  const { data: teams, isLoading } = useFleetYawMeasurements(eventId);

  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);
  const [comparedTeams, toggleCompare] = useToggleSet<string>();

  const toggleExpand = (code: string) => {
    setExpandedTeam((prev) => (prev === code ? null : code));
  };

  const enriched = useMemo(() => {
    if (!teams) return [];
    return teams.map((t) => {
      const hasCheck = !!t.latest_check;
      const prt = hasCheck ? computeSide(extractSide(t.latest_check!, "prt")) : null;
      const stb = hasCheck ? computeSide(extractSide(t.latest_check!, "stb")) : null;
      const avgStatic = prt && stb ? (prt.tot_yaw + stb.tot_yaw) / 2 : null;
      const status: YawStatus | "none" = avgStatic != null ? computeYawStatus(avgStatic) : "none";
      const trendData = computeTrendData(t.history);
      return { ...t, prt, stb, avgStatic, status, trendData };
    });
  }, [teams]);

  const summary = useMemo(() => {
    const total = enriched.length;
    const checked = enriched.filter((t) => t.status !== "none").length;
    const inSpec = enriched.filter((t) => t.status === "in-spec").length;
    const statics = enriched.filter((t) => t.avgStatic != null).map((t) => t.avgStatic!);
    const fleetAvg = statics.length ? statics.reduce((a, b) => a + b, 0) / statics.length : null;
    return { total, checked, inSpec, fleetAvg };
  }, [enriched]);

  const allEvents = useMemo(() => {
    const evMap = new Map<string, string>();
    enriched.forEach((t) => t.trendData.forEach((d) => { if (!evMap.has(d.event)) evMap.set(d.event, d.eventDate); }));
    return [...evMap.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([name]) => name);
  }, [enriched]);

  const comparedData = useMemo(() => {
    return [...comparedTeams].map((code) => {
      const t = enriched.find((e) => e.team_code === code);
      return { code, data: t?.trendData ?? [] };
    }).filter((d) => d.data.length > 0);
  }, [comparedTeams, enriched]);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Yaw" subtitle={event?.name} />
        <div className="mt-6 space-y-2">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Yaw" subtitle={event?.name} count={enriched.length} countLabel="teams" />

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <CheckedCard checked={summary.checked} total={summary.total} />
        <div className="bg-card border-l-3 border-l-green-500 rounded-r-lg pl-4 pr-5 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center flex-shrink-0">
            <IconCheck className="h-4 w-4 text-emerald-500" />
          </div>
          <div>
            <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">In Spec</div>
            <div className="text-lg font-bold">{summary.inSpec} <span className="text-sm font-normal text-muted-foreground">/ {summary.checked}</span></div>
          </div>
        </div>
        <div className="bg-card border-l-3 border-l-border rounded-r-lg pl-4 pr-5 py-3 flex items-center gap-3">
          <div>
            <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">Fleet Avg</div>
            <div className="text-lg font-bold tabular-nums">{summary.fleetAvg != null ? `${summary.fleetAvg.toFixed(2)}°` : "—"}</div>
            <div className="text-[10px] text-muted-foreground">Target: {TARGET_YAW_ANGLE.toFixed(2)}°</div>
          </div>
        </div>
      </div>

      {comparedTeams.size > 0 && comparedData.length > 0 && (
        <div className="mt-6 bg-card rounded-lg border overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold">Fleet Comparison</span>
              <span className="text-[10px] text-muted-foreground">Static yaw + min–max across events</span>
            </div>
            <ComparisonTeamPills
              codes={comparedTeams}
              teamLookup={(code) => enriched.find((e) => e.team_code === code)}
              onRemove={toggleCompare}
            />
          </div>
          <div className="grid grid-cols-2">
            <div className="p-4 border-r">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-red-400 mb-2">Port</div>
              <ComparisonSideChart teams={comparedData} allEvents={allEvents} sideKey="prt" />
            </div>
            <div className="p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500 mb-2">Starboard</div>
              <ComparisonSideChart teams={comparedData} allEvents={allEvents} sideKey="stb" />
            </div>
          </div>
        </div>
      )}

      {/* Team rows */}
      <div className="mt-6 flex flex-col gap-2">
        {enriched.map((t) => {
          const s = statusMeta(t.status);
          const hasData = t.status !== "none";
          const isExpanded = expandedTeam === t.team_code;
          const isCompared = comparedTeams.has(t.team_code);
          const badge = (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${s.bg} ${s.text}`}>{s.label}</span>
          );
          const ageEl = (
            <span className="text-xs text-muted-foreground whitespace-nowrap">{hasData && t.latest_check ? timeAgo(t.latest_check.checked_at) : "—"}</span>
          );
          const chevron = hasData ? (
            <IconChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`} />
          ) : null;

          return (
            <div key={t.team_code} className={`border-l-3 rounded-r-lg bg-card ${s.border}`}>
              <div
                className={`${FLEET_ROW_INNER} ${isExpanded ? "shadow-[0_1px_4px_rgba(0,0,0,0.04)]" : ""}`}
                onClick={() => hasData && toggleExpand(t.team_code)}
              >
                <div className="flex items-center gap-3 sm:w-28 sm:shrink-0">
                  <div className="w-4 flex items-center justify-center shrink-0">
                    {hasData && (
                      <input type="checkbox" className="w-3.5 h-3.5 rounded border-border accent-blue-500 cursor-pointer"
                        checked={isCompared}
                        onChange={() => toggleCompare(t.team_code)}
                        onClick={(e) => e.stopPropagation()} />
                    )}
                  </div>
                  <TeamFlag iso_alpha2={t.team_iso_alpha2} size="xs" />
                  <span className="font-medium text-[13px]">{t.team_code}</span>
                  {/* Mobile only: status + age sit on the identity line */}
                  <div className="flex items-center gap-2 ml-auto sm:hidden">
                    {hasData && badge}
                    {ageEl}
                    {chevron}
                  </div>
                </div>

                <div className="min-w-0 sm:flex-1 sm:px-3">
                  {hasData && t.latest_check ? (
                    <div className="flex flex-col gap-1">
                      <GaugeRow check={t.latest_check} side="prt" />
                      <GaugeRow check={t.latest_check} side="stb" />
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">No yaw data</span>
                  )}
                </div>

                <div className="w-[180px] shrink-0 px-2 hidden md:block">
                  <InlineTrend data={t.trendData} status={t.status === "none" ? "in-spec" : t.status} />
                </div>

                <div className="w-20 shrink-0 hidden sm:block">{badge}</div>

                <div className="hidden sm:flex items-center gap-2 w-24 shrink-0 justify-end">
                  {ageEl}
                  {chevron}
                </div>
              </div>

              {isExpanded && hasData && (
                <div className="border-t border-border/50 px-4 py-3">
                  <HistoryTable entry={t} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
